import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ActionIcon, Tooltip } from '@mantine/core';
import { IconMap } from '@tabler/icons-react';
import './styles.css';

interface DraggableSitemapButtonProps {
  onClick: () => void;
}

const STORAGE_KEY = 'sitemap-fab-position';
const DEFAULT_POSITION = { bottom: 24, right: 24 }; // bottom-right default

export function DraggableSitemapButton({ onClick }: DraggableSitemapButtonProps) {
  const [position, setPosition] = useState<{ bottom?: number; right?: number; top?: number; left?: number }>(DEFAULT_POSITION);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [hasDragged, setHasDragged] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Load saved position from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setPosition(parsed);
      } catch (e) {
        // Invalid saved data, use default
        setPosition(DEFAULT_POSITION);
      }
    }
  }, []);

  // Save position to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(position));
  }, [position]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      setStartPos({ x: e.clientX, y: e.clientY });
      setIsDragging(true);
      setHasDragged(false);
    }
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!buttonRef.current) return;

      const buttonWidth = buttonRef.current.offsetWidth;
      const buttonHeight = buttonRef.current.offsetHeight;
      
      // Calculate position from top-left
      const newLeft = e.clientX - dragOffset.x;
      const newTop = e.clientY - dragOffset.y;

      // Constrain to viewport bounds
      const maxLeft = window.innerWidth - buttonWidth;
      const maxTop = window.innerHeight - buttonHeight;
      const minLeft = 0;
      const minTop = 0;

      const constrainedLeft = Math.max(minLeft, Math.min(maxLeft, newLeft));
      const constrainedTop = Math.max(minTop, Math.min(maxTop, newTop));

      // Convert to bottom/right for storage (easier to work with)
      const bottom = window.innerHeight - constrainedTop - buttonHeight;
      const right = window.innerWidth - constrainedLeft - buttonWidth;

      setPosition({
        bottom,
        right,
        top: constrainedTop,
        left: constrainedLeft,
      });
      
      // Mark that we've dragged (moved more than a few pixels from start)
      setHasDragged((prev) => {
        if (prev) return prev;
        const moved = Math.abs(e.clientX - startPos.x) > 5 || Math.abs(e.clientY - startPos.y) > 5;
        return moved;
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      // Reset hasDragged after a short delay to allow click detection
      setTimeout(() => setHasDragged(false), 100);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset, startPos]);

  const handleClick = () => {
    // Only trigger onClick if we didn't just drag
    if (!hasDragged) {
      onClick();
    }
  };

  // Use top/left if available (during drag), otherwise use bottom/right
  const style: React.CSSProperties = {
    position: 'fixed',
    cursor: isDragging ? 'grabbing' : 'grab',
  };

  if (position.top !== undefined && position.left !== undefined) {
    style.top = `${position.top}px`;
    style.left = `${position.left}px`;
    style.bottom = 'auto';
    style.right = 'auto';
  } else {
    style.bottom = `${position.bottom ?? DEFAULT_POSITION.bottom}px`;
    style.right = `${position.right ?? DEFAULT_POSITION.right}px`;
    style.top = 'auto';
    style.left = 'auto';
  }

  const buttonElement = (
    <Tooltip label="Open Sitemap (Drag to reposition)" position="left" withArrow>
      <ActionIcon
        ref={buttonRef}
        className={`sitemap-fab ${isDragging ? 'dragging' : ''}`}
        size="xl"
        radius="xl"
        color="violet"
        variant="filled"
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        aria-label="Open sitemap"
        style={style}
      >
        <IconMap size={24} />
      </ActionIcon>
    </Tooltip>
  );

  // Use portal to render at document body level to avoid any parent container issues
  return typeof document !== 'undefined' ? createPortal(buttonElement, document.body) : null;
}

